import { EXTENSIONS_BY_DOCUMENT_TYPE } from '../../constants/externalDocuments';
import type { IActivationPriceMismatch } from '../../hooks/useActivationCensusFlow/activationCensus';
import type {
  ICommercialAddon,
  ICommercialAddonGroupEntry,
  ICommercialAddonsResponse,
  ICommercialPlanBundle,
  IActivationCensusValidationErrorsResult,
  IPriceCalculationDistributionInput,
  IPriceCalculationProductInput,
  IPriceCalculationTierInput,
  IPriceStructureTier,
  IQuote,
  IQuoteCommercialAddon,
  IQuoteCommercialAddonInput,
  IQuotePriceCalculationInput,
  IQuotePriceCalculationResult,
  IQuickCensusAgeSummaryEntry,
  ICensusValidationResult,
  IQuoteCensusFileState,
  IQuoteFile,
  IUpdateQuoteInput
} from '../../interfaces/quotes';
import type { IUserInfoResult } from '../../interfaces/user';
import { Quotes } from '../../services/api/quotes/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../translations/prospects';
import { TRANSLATIONS_QUOTES } from '../../translations/quotes';
import {
  CensusVariant,
  CommercialAddonType,
  CommercialPlanBundleType,
  QuoteDocumentType,
  type AddonTypeById,
  type AgeInputMode,
  type ExternalBenefitsState,
  type TobaccoStatus
} from '../../types/quotes';
import { round2 } from '../numbers/numbers';

export const MIN_AGE = 18;
export const MAX_AGE = 80;

export const isVoluntaryQuote = (quote: IQuote, addonTypeById: AddonTypeById): boolean =>
  quote.commercialAddons.some((addon) => addonTypeById.get(addon.commercialAddonId) === CommercialAddonType.Voluntary);

export const buildQuoteUpdateBody = (quote: IQuote, overrides: Partial<IUpdateQuoteInput> = {}): IUpdateQuoteInput => ({
  employee: quote.employee,
  name: quote.name,
  segment: quote.segment,
  startDate: quote.startDate,
  frequency: quote.frequency,
  paymentDate: quote.paymentDate,
  workforceSize: quote.workforceSize,
  status: quote.status,
  registerNumber: quote.registerNumber,
  clientActivationId: quote.clientActivationId,
  validationRequestId: quote.validationRequestId,
  files: quote.files.map((file) => ({ id: file.id, fileJson: file.fileJson })),
  commercialAddons: quote.commercialAddons.map((addon) => ({
    id: addon.id,
    quoteId: addon.quoteId,
    commercialAddonId: addon.commercialAddonId,
    entity: addon.entity,
    tobaccoStatus: addon.tobaccoStatus,
    averageEmployeeAge: addon.averageEmployeeAge,
    priceStructureId: addon.priceStructureId,
    registerNumber: addon.registerNumber,
    payment: addon.payment
      ? {
          id: addon.payment.id,
          quoteCommercialAddonId: addon.payment.quoteCommercialAddonId,
          ageDistribution: addon.payment.ageDistribution as Record<string, unknown> | null,
          commissionPercentage: addon.payment.commissionPercentage,
          commission: addon.payment.commission,
          total: addon.payment.total,
          registerNumber: addon.payment.registerNumber
        }
      : null
  })),
  ...overrides
});

export const isInactiveQuote = (quote: IQuote): boolean => quote.status === 'inactive';

export const excludeInactiveQuotes = (quotes: IQuote[]): IQuote[] => quotes.filter((quote) => !isInactiveQuote(quote));

export const isQuickCensusFile = (file: IQuoteFile): boolean => file.fileType === 'census_simple';

export const isActivationCensusFile = (file: IQuoteFile): boolean => file.fileType === 'census';

export const findActivationCensusFile = (quote: IQuote): IQuoteFile | undefined =>
  quote.files?.find(isActivationCensusFile);

export const findAppliedCensusFile = (quote: IQuote): IQuoteFile | undefined =>
  quote.files?.find(
    (file) => isActivationCensusFile(file) && file.fileJson?.applied === true && !!file.fileJson?.appliedAt
  );

export const quoteHasAppliedCensus = (quote: IQuote): boolean => findAppliedCensusFile(quote) !== undefined;

export const isExternalBenefitsFile = (file: IQuoteFile): boolean => file.fileType === 'external';

export const quoteHasExternalBenefits = (quote: IQuote): boolean => quote.files?.some(isExternalBenefitsFile) ?? false;

export const getExternalFiles = (quote: IQuote): IQuoteFile[] => quote.files?.filter(isExternalBenefitsFile) ?? [];

const isKnownDocumentType = (value: unknown): value is QuoteDocumentType =>
  typeof value === 'string' && (Object.values(QuoteDocumentType) as string[]).includes(value);

export const getExternalDocumentType = (file: IQuoteFile): QuoteDocumentType => {
  const documentType = file.fileJson?.documentType;
  return isKnownDocumentType(documentType) ? documentType : QuoteDocumentType.Other;
};

const baseFileName = (path: string): string => path.split('/').pop() ?? path;

export const getExternalOriginalName = (file: IQuoteFile): string => {
  const stored = file.fileJson?.fileName;
  return typeof stored === 'string' && stored ? stored : baseFileName(file.fileName);
};

export const getExternalBenefitsState = (quote: IQuote): ExternalBenefitsState =>
  quoteHasExternalBenefits(quote) ? 'in_review' : 'none';

export const getExternalBenefitsSubmittedAt = (quote: IQuote): string | null =>
  getExternalFiles(quote)
    .map((file) => file.fileJson?.uploadedAt)
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .reduce<string | null>((latest, current) => (latest === null || current > latest ? current : latest), null);

export const groupExternalFilesByType = (quote: IQuote): Record<QuoteDocumentType, IQuoteFile[]> => {
  const groups = Object.fromEntries(
    Object.values(QuoteDocumentType).map((type) => [type, [] as IQuoteFile[]])
  ) as Record<QuoteDocumentType, IQuoteFile[]>;

  for (const file of getExternalFiles(quote)) {
    groups[getExternalDocumentType(file)].push(file);
  }

  return groups;
};

const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
};

export const isAllowedForDocumentType = (fileName: string, documentType: QuoteDocumentType): boolean =>
  EXTENSIONS_BY_DOCUMENT_TYPE[documentType].includes(extensionOf(fileName));

export const readExtensions = (extensions: readonly string[]): string => {
  const dotted = extensions.map((extension) => `.${extension}`);
  if (dotted.length <= 1) return dotted.join('');
  return `${dotted.slice(0, -1).join(', ')} or ${dotted.at(-1)}`;
};

const tierMatchesAge = (tier: IPriceStructureTier, age: number): boolean => age >= tier.age.from && age <= tier.age.to;

export const groupKey = (type: CommercialAddonType, groupId: number): string => `${type}-${groupId}`;

export const addonOptionLabel = (groupName: string, addonName: string): string => {
  const prefix = `${groupName} — `;
  return addonName.startsWith(prefix) ? addonName.slice(prefix.length) : addonName;
};

export const ageBandLabel = (age: number, addon?: ICommercialAddon | null): string | null => {
  if (!age) return null;
  if (addon) {
    const tier = (addon.priceStructure?.structure ?? []).find((t) => age >= t.age.from && age <= t.age.to);
    return tier ? `${tier.age.from}-${tier.age.to}` : null;
  }
  if (age < MIN_AGE) return null;
  if (age <= 29) return '18-29';
  if (age <= 39) return '30-39';
  if (age <= 49) return '40-49';
  if (age <= 59) return '50-59';
  return '60+';
};

/** Min/max age bounds derived from an addon's priceStructure tiers. */
export const getAddonAgeRange = (addon: ICommercialAddon | null | undefined): { min: number; max: number } | null => {
  const tiers = addon?.priceStructure?.structure ?? [];
  if (!tiers.length) return null;
  return {
    min: Math.min(...tiers.map((t) => t.age.from)),
    max: Math.max(...tiers.map((t) => t.age.to))
  };
};

export const addonRequiresTobacco = (addon: ICommercialAddon | null | undefined): boolean => {
  const tiers = addon?.priceStructure?.structure ?? [];
  const hasTobacco = tiers.some((tier) => tier.other_factors?.tobacco === true);
  const hasNonTobacco = tiers.some((tier) => tier.other_factors?.tobacco !== true);
  return hasTobacco && hasNonTobacco;
};

export const groupRequiresTobacco = (entry: ICommercialAddonGroupEntry): boolean =>
  entry.addons.some(addonRequiresTobacco);

export const addonIsAgeDependent = (addon: ICommercialAddon | null | undefined): boolean => {
  if (!addon) return false;
  const tiers = addon.priceStructure?.structure ?? [];
  const nonTobaccoTiers = tiers.filter((tier) => tier.other_factors?.tobacco !== true);
  const cohort = nonTobaccoTiers.length ? nonTobaccoTiers : tiers;
  return new Set(cohort.map((tier) => tier.base_price)).size > 1;
};

export const getAddonRate = (
  addon: ICommercialAddon | null | undefined,
  age: number,
  tobaccoStatus: TobaccoStatus
): number | null => {
  if (!addon) return null;
  const tiers = addon.priceStructure?.structure;
  if (!tiers?.length) return addon.suggestedPrice ?? null;

  if (!age) {
    if (addonIsAgeDependent(addon)) return null;
    const flatTier = tiers.find((tier) => tier.other_factors?.tobacco !== true) ?? tiers[0];
    return flatTier?.base_price ?? null;
  }

  const inBand = tiers.filter((tier) => tierMatchesAge(tier, age));
  if (!inBand.length) return null;

  const hasTobaccoVariants = inBand.some((tier) => tier.other_factors?.tobacco === true);
  if (!hasTobaccoVariants) {
    return inBand[0].base_price ?? null;
  }

  const tobaccoTier = inBand.find((tier) => tier.other_factors?.tobacco === true);
  const nonTobaccoTier = inBand.find((tier) => tier.other_factors?.tobacco !== true);

  if (!nonTobaccoTier) return tobaccoTier?.base_price ?? null;
  if (!tobaccoTier) return nonTobaccoTier.base_price ?? null;

  if (tobaccoStatus === 'tobacco') return tobaccoTier.base_price ?? null;
  if (tobaccoStatus === 'non_tobacco') return nonTobaccoTier.base_price ?? null;

  const withTobacco = tobaccoTier.base_price;
  const withoutTobacco = nonTobaccoTier.base_price;
  if (withTobacco == null || withoutTobacco == null) return null;
  return round2((withTobacco + withoutTobacco) / 2);
};

// ─── Product groups (flattened view over the addons response) ─────────────────

export interface IProductGroup {
  key: string;
  type: CommercialAddonType;
  name: string;
  description: string | null;
  addons: ICommercialAddon[];
  requiresTobacco: boolean;
  employerPaid: boolean;
  ageDependent: boolean;
}

export const buildProductGroups = (addons: ICommercialAddonsResponse): IProductGroup[] =>
  addons.flatMap((typeGroup) =>
    typeGroup.groups.map((entry) => ({
      key: groupKey(typeGroup.type, entry.group.id),
      type: typeGroup.type,
      name: entry.group.name ?? '',
      description: entry.group.description,
      addons: entry.addons,
      requiresTobacco: groupRequiresTobacco(entry),
      employerPaid: typeGroup.type !== CommercialAddonType.Voluntary,
      ageDependent: entry.addons.some(addonIsAgeDependent)
    }))
  );

// ─── Selection state ─────────────────────────────────────────────────────────

export interface IGroupSelection {
  selected: boolean;
  addonId: number | null;
  tobaccoStatus: TobaccoStatus;
  ageInputMode: AgeInputMode;
}

export type SelectionState = Record<string, IGroupSelection>;

export const initSelectionState = (groups: IProductGroup[]): SelectionState =>
  Object.fromEntries(
    groups.map((group): [string, IGroupSelection] => [
      group.key,
      {
        selected: !group.employerPaid,
        addonId: group.addons[0]?.id ?? null,
        tobaccoStatus: 'non_tobacco',
        ageInputMode: 'average'
      }
    ])
  );

export const buildSelectionStateFromQuote = (groups: IProductGroup[], quote: IQuote): SelectionState => {
  const quoteAddons = quote.commercialAddons.filter((qa) => qa.entity !== 'plan');
  const quoteAddonIds = new Set(quoteAddons.map((qa) => qa.commercialAddonId));
  const quoteAddonMap = new Map(quoteAddons.map((qa) => [qa.commercialAddonId, qa]));
  const hasCensus = quote.files?.some(isQuickCensusFile);

  return Object.fromEntries(
    groups.map((group): [string, IGroupSelection] => {
      const matchingAddon = group.addons.find((addon) => quoteAddonIds.has(addon.id));
      const quoteAddon = matchingAddon ? quoteAddonMap.get(matchingAddon.id) : null;

      const hasAverageAgeSaved = quoteAddon?.averageEmployeeAge != null;
      const isCensusTarget =
        hasCensus && !hasAverageAgeSaved && matchingAddon && group.employerPaid && addonIsAgeDependent(matchingAddon);

      return [
        group.key,
        {
          selected: !!matchingAddon,
          addonId: matchingAddon?.id ?? group.addons[0]?.id ?? null,
          tobaccoStatus: (quoteAddon?.tobaccoStatus as TobaccoStatus) ?? 'non_tobacco',
          ageInputMode: isCensusTarget ? 'census' : 'average'
        }
      ];
    })
  );
};

export interface IQuoteEditState {
  quoteName: string;
  segment: string;
  selection: SelectionState;
  censusFile: IQuoteCensusFileState | null;
  censusSummaries: Record<string, IQuickCensusAgeSummaryEntry[]>;
  employeesStr: string;
  averageAgeStr: string;
  initialPriceFactorsStr: string;
}

export const extractEditQuoteState = (
  editingQuote: IQuote,
  productGroups: IProductGroup[],
  matchedWorkforceSize?: number | null
): IQuoteEditState => {
  const quoteName = editingQuote.name || '';
  const segment = editingQuote.segment || '';

  const existingCensusFile = editingQuote.files?.find(isQuickCensusFile);

  const selection = buildSelectionStateFromQuote(productGroups, editingQuote);

  const censusSummaries: Record<string, IQuickCensusAgeSummaryEntry[]> = {};
  let censusFileState: IQuoteCensusFileState | null = null;

  if (existingCensusFile) {
    const distribution = existingCensusFile.fileJson?.distribution;
    censusFileState = {
      id: existingCensusFile.id,
      fileJson: existingCensusFile.fileJson,
      fileName: existingCensusFile.fileName,
      rowCount: distribution ? distribution.reduce((sum, item) => sum + (item.quantity || 0), 0) : undefined
    };

    if (distribution && Array.isArray(distribution)) {
      productGroups.forEach((group) => {
        if (selection[group.key]?.ageInputMode === 'census') {
          censusSummaries[group.key] = distribution;
        }
      });
    }
  }

  const editingWorkforceSize = editingQuote.workforceSize ?? matchedWorkforceSize;
  const employeesStr = editingWorkforceSize == null ? '' : String(editingWorkforceSize);

  const addonWithAge = editingQuote.commercialAddons.find((ca) => ca.averageEmployeeAge != null);
  const averageAgeStr = addonWithAge?.averageEmployeeAge == null ? '' : String(addonWithAge.averageEmployeeAge);

  const employeesNum = Number.parseInt(employeesStr, 10) || 0;
  const ageNum = Number.parseInt(averageAgeStr, 10) || 0;

  const initialPriceFactorsStr = JSON.stringify({
    selection,
    employees: employeesNum,
    age: ageNum,
    censusSummaries
  });

  return {
    quoteName,
    segment,
    selection,
    censusFile: censusFileState,
    censusSummaries,
    employeesStr,
    averageAgeStr,
    initialPriceFactorsStr
  };
};

export interface ISummaryLine {
  key: string;
  groupName: string;
  label: string;
  ratePerEmployee: number;
  employees: number;
  total: number;
  commissionPct: number;
  commission: number;
  ratePerEmployeeIsAverage?: boolean;
}

export interface IQuoteSummary {
  employerLines: ISummaryLine[];
  voluntaryLines: ISummaryLine[];
  employerTotal: number;
  brokerCommission: number;
  grandTotal: number;
  hasMissingRate: boolean;
}

const findAddon = (group: IProductGroup, addonId: number | null): ICommercialAddon | undefined =>
  group.addons.find((addon) => addon.id === addonId);

interface IFallbackLineResult {
  line: ISummaryLine | null;
  missingRate: boolean;
  employerTotal?: number;
  brokerCommission?: number;
}

const censusRateIsAveraged = (
  group: IProductGroup,
  entry: IGroupSelection,
  addon: ICommercialAddon,
  censusSummaries?: Record<string, IQuickCensusAgeSummaryEntry[]>
): boolean => {
  if (entry.ageInputMode !== 'census' || !addonIsAgeDependent(addon)) return false;
  const censusSummary = censusSummaries?.[group.key];
  if (!censusSummary?.length) return false;
  return groupSummaryIntoBands(censusSummary, addon).length > 1;
};

const buildFallbackSummaryLine = (
  group: IProductGroup,
  entry: IGroupSelection,
  addon: ICommercialAddon,
  employees: number,
  age: number,
  censusSummaries?: Record<string, IQuickCensusAgeSummaryEntry[]>
): IFallbackLineResult => {
  let total = 0;
  let safeRate = 0;

  if (entry.ageInputMode === 'census') {
    const censusSummary = censusSummaries?.[group.key];
    if (!censusSummary || censusSummary.length === 0) {
      return { line: null, missingRate: group.employerPaid };
    }
    let hasBucketMissingRate = false;
    total = censusSummary.reduce((sum, bucket) => {
      const bucketRate = getAddonRate(addon, bucket.age, entry.tobaccoStatus);
      if (bucketRate == null) hasBucketMissingRate = true;
      return sum + (bucketRate ?? 0) * bucket.quantity;
    }, 0);
    if (hasBucketMissingRate && group.employerPaid) {
      return { line: null, missingRate: true };
    }
    const totalPeople = censusSummary.reduce((sum, bucket) => sum + bucket.quantity, 0);
    safeRate = totalPeople > 0 ? total / totalPeople : 0;
    total = round2(total);
  } else {
    const rate = getAddonRate(addon, age, entry.tobaccoStatus);
    if (rate == null && group.employerPaid) {
      return { line: null, missingRate: true };
    }
    safeRate = rate ?? 0;
    total = round2(safeRate * employees);
  }

  const commission = round2(total * addon.commission);
  const line: ISummaryLine = {
    key: group.key,
    groupName: group.name,
    label: addon.name,
    ratePerEmployee: safeRate,
    employees,
    total,
    commissionPct: addon.commission,
    commission,
    ratePerEmployeeIsAverage: censusRateIsAveraged(group, entry, addon, censusSummaries)
  };
  return { line, missingRate: false };
};

export const buildSummary = (
  groups: IProductGroup[],
  selection: SelectionState,
  employees: number,
  age: number,
  price?: IQuotePriceCalculationResult | null,
  censusSummaries?: Record<string, IQuickCensusAgeSummaryEntry[]>
): IQuoteSummary => {
  const employerLines: ISummaryLine[] = [];
  const voluntaryLines: ISummaryLine[] = [];
  let hasMissingRate = false;

  if (price) {
    const priceMap = new Map(price.products.map((p) => [p.productId, p]));
    groups.forEach((group) => {
      const entry = selection[group.key];
      if (!entry?.selected) return;
      const addon = findAddon(group, entry.addonId);
      if (!addon) return;
      const priceInfo = priceMap.get(addon.id);

      if (!priceInfo && entry.ageInputMode === 'census') {
        if (group.employerPaid) hasMissingRate = true;
        return;
      }

      const fallbackRate = priceInfo ? null : getAddonRate(addon, age, entry.tobaccoStatus);
      if (!priceInfo && fallbackRate == null && group.employerPaid) {
        hasMissingRate = true;
        return;
      }
      const total = priceInfo?.total ?? round2((fallbackRate ?? 0) * employees);
      const commission = priceInfo?.commission_total ?? round2(total * addon.commission);

      let rate = fallbackRate ?? 0;
      if (priceInfo) {
        rate = employees > 0 ? total / employees : 0;
      }

      const line: ISummaryLine = {
        key: group.key,
        groupName: group.name,
        label: addon.name,
        ratePerEmployee: rate,
        employees,
        total,
        commissionPct: addon.commission,
        commission,
        ratePerEmployeeIsAverage: censusRateIsAveraged(group, entry, addon, censusSummaries)
      };

      if (group.employerPaid) {
        employerLines.push(line);
      } else {
        voluntaryLines.push(line);
      }
    });

    const employerTotal = employerLines.reduce((sum, l) => sum + l.total, 0);
    const brokerCommission = employerLines.reduce((sum, l) => sum + l.commission, 0);

    return {
      employerLines,
      voluntaryLines,
      employerTotal,
      brokerCommission,
      grandTotal: employerTotal,
      hasMissingRate
    };
  }

  let employerTotal = 0;
  let brokerCommission = 0;
  groups.forEach((group) => {
    const entry = selection[group.key];
    if (!entry?.selected) return;
    const addon = findAddon(group, entry.addonId);
    if (!addon) return;

    const result = buildFallbackSummaryLine(group, entry, addon, employees, age, censusSummaries);
    if (result.missingRate) {
      hasMissingRate = true;
      return;
    }
    if (!result.line) return;

    if (group.employerPaid) {
      employerLines.push(result.line);
      employerTotal = round2(employerTotal + result.line.total);
      brokerCommission = round2(brokerCommission + result.line.commission);
    } else {
      voluntaryLines.push(result.line);
    }
  });

  return {
    employerLines,
    voluntaryLines,
    employerTotal,
    brokerCommission,
    grandTotal: employerTotal,
    hasMissingRate
  };
};

export const filterBundlesBySalesModel = (
  bundles: ICommercialPlanBundle[],
  appOnly: boolean
): ICommercialPlanBundle[] => {
  const type = appOnly ? CommercialPlanBundleType.AppOnly : CommercialPlanBundleType.Insurance;
  return bundles.filter((bundle) => bundle.type === type);
};

export const buildBundleSummaryLine = (bundle: ICommercialPlanBundle, employees: number): ISummaryLine => {
  const ratePerEmployee = bundle.suggestedPrice ?? 0;
  return {
    key: `bundle-${bundle.id}`,
    groupName: bundle.name,
    label: '',
    ratePerEmployee,
    employees,
    total: round2(ratePerEmployee * employees),
    commissionPct: 0,
    commission: 0
  };
};

const withExistingCommercialAddon = (
  payload: IQuoteCommercialAddonInput,
  existing?: IQuoteCommercialAddon
): IQuoteCommercialAddonInput => {
  if (!existing) return payload;

  payload.id = existing.id;
  payload.quoteId = existing.quoteId;
  payload.registerNumber = existing.registerNumber;

  if (existing.payment && payload.payment) {
    payload.payment.id = existing.payment.id;
    payload.payment.quoteCommercialAddonId = existing.payment.quoteCommercialAddonId;
    payload.payment.ageDistribution = existing.payment.ageDistribution as Record<string, unknown> | null;
    payload.payment.registerNumber = existing.payment.registerNumber;
  }

  return payload;
};

export const buildCommercialAddonsPayload = (
  groups: IProductGroup[],
  selection: SelectionState,
  employees: number,
  age: number,
  summary: IQuoteSummary,
  existingQuote?: IQuote | null,
  selectedBundle?: ICommercialPlanBundle | null
): IQuoteCommercialAddonInput[] => {
  const existingAddonMap = new Map(
    existingQuote?.commercialAddons.filter((qa) => qa.entity !== 'plan').map((qa) => [qa.commercialAddonId, qa]) ?? []
  );
  const existingBundleMap = new Map(
    existingQuote?.commercialAddons.filter((qa) => qa.entity === 'plan').map((qa) => [qa.commercialAddonId, qa]) ?? []
  );

  const addonPayloads = groups
    .map((group): IQuoteCommercialAddonInput | null => {
      const entry = selection[group.key];
      if (!entry?.selected || entry.addonId == null) return null;
      const addon = findAddon(group, entry.addonId);
      if (!addon) return null;

      const line = group.employerPaid
        ? summary.employerLines.find((l) => l.key === group.key)
        : summary.voluntaryLines.find((l) => l.key === group.key);

      const payload: IQuoteCommercialAddonInput = {
        commercialAddonId: addon.id,
        entity: 'addon',
        averageEmployeeAge: entry.ageInputMode === 'average' ? age || null : null,
        tobaccoStatus: addonRequiresTobacco(addon) ? entry.tobaccoStatus : null,
        priceStructureId: addon.priceStructure?.id ?? null,
        payment: {
          commissionPercentage: addon.commission,
          commission: line?.commission ?? 0,
          total: line?.total ?? 0
        }
      };

      return withExistingCommercialAddon(payload, existingAddonMap.get(addon.id));
    })
    .filter((addon): addon is IQuoteCommercialAddonInput => addon !== null);

  if (!selectedBundle) return addonPayloads;

  const bundlePayload: IQuoteCommercialAddonInput = {
    commercialAddonId: selectedBundle.id,
    entity: 'plan',
    payment: {
      commissionPercentage: 0,
      commission: 0,
      total: buildBundleSummaryLine(selectedBundle, employees).total
    }
  };

  return [withExistingCommercialAddon(bundlePayload, existingBundleMap.get(selectedBundle.id)), ...addonPayloads];
};

export const buildEmployeeName = (name?: string, lastName?: string): string =>
  [name, lastName].filter(Boolean).join(' ').trim();

export interface IBuildSaveQuotePayloadParams {
  isEditing: boolean;
  editingQuote: IQuote | null;
  userInfo?: Pick<IUserInfoResult, 'name' | 'lastName' | 'email'> | null;
  quoteName: string;
  segment: string;
  groups: IProductGroup[];
  selection: SelectionState;
  employees: number;
  age: number;
  summary: IQuoteSummary;
  censusFile: IQuoteCensusFileState | null;
  selectedBundle?: ICommercialPlanBundle | null;
}

export const buildSaveQuotePayload = ({
  isEditing,
  editingQuote,
  userInfo,
  quoteName,
  segment,
  groups,
  selection,
  employees,
  age,
  summary,
  censusFile,
  selectedBundle
}: IBuildSaveQuotePayloadParams): IUpdateQuoteInput => ({
  employee: buildEmployeeName(userInfo?.name, userInfo?.lastName) || (userInfo?.email ?? ''),
  name: quoteName.trim(),
  segment: segment.trim() || null,
  commercialAddons: buildCommercialAddonsPayload(
    groups,
    selection,
    employees,
    age,
    summary,
    editingQuote,
    selectedBundle
  ),
  workforceSize: employees || null,
  ...(censusFile &&
    Object.values(selection).some((s) => s.ageInputMode === 'census') && {
      files: [{ id: censusFile.id, fileJson: censusFile.fileJson }]
    }),
  ...(isEditing &&
    editingQuote && {
      status: editingQuote.status,
      frequency: editingQuote.frequency,
      registerNumber: editingQuote.registerNumber,
      startDate: editingQuote.startDate
    })
});

const getTobaccoAnswers = (isWellness: boolean, requiresTobacco: boolean, tobaccoStatus: TobaccoStatus) => {
  if (isWellness) return null;
  if (!requiresTobacco) return [];

  let answerStr: string = tobaccoStatus;
  if (tobaccoStatus === 'tobacco') answerStr = 'Y';
  else if (tobaccoStatus === 'non_tobacco') answerStr = 'N';

  return [{ questionCode: 'profile-tobacco', answer: answerStr }];
};

const buildPriceStructure = (addon: ICommercialAddon, isVoluntary: boolean): IPriceCalculationTierInput[] => {
  const structure = addon.priceStructure?.structure;
  if (!structure?.length) {
    return [{ base_price: addon.suggestedPrice }];
  }

  return structure.map((tier) => ({
    ...(isVoluntary ? { age: tier.age ?? { from: 0, to: 0 } } : tier.age != null && { age: tier.age }),
    ...(tier.other_factors != null && { other_factors: tier.other_factors }),
    base_price: tier.base_price ?? 0,
    ...(tier.price_partner ? { price_partner: tier.price_partner } : {}),
    ...(tier.price_family ? { price_family: tier.price_family } : {}),
    ...(tier.price_partner_and_family ? { price_partner_and_family: tier.price_partner_and_family } : {})
  }));
};

export const buildPriceCalculationRequest = (
  groups: IProductGroup[],
  selection: SelectionState,
  employees: number,
  age: number,
  censusSummaries?: Record<string, IQuickCensusAgeSummaryEntry[]>
): IQuotePriceCalculationInput => {
  const insurance: IPriceCalculationProductInput[] = [];
  const wellness: IPriceCalculationProductInput[] = [];

  groups.forEach((group) => {
    const entry = selection[group.key];
    if (!entry?.selected || entry.addonId == null) return;
    const addon = group.addons.find((a) => a.id === entry.addonId);
    if (!addon) return;

    const isWellness = group.type === CommercialAddonType.Wellness;
    const isVoluntary = group.type === CommercialAddonType.Voluntary;
    const requiresTobacco = addonRequiresTobacco(addon);

    const censusSummary = censusSummaries?.[group.key];
    const useCensusDistribution = censusSummary && censusSummary.length > 0 && entry.ageInputMode === 'census';

    if (entry.ageInputMode === 'census' && (!censusSummary || censusSummary.length === 0)) {
      return;
    }
    const distribution: IPriceCalculationDistributionInput[] = useCensusDistribution
      ? censusSummary.map((s) => ({
          age: isWellness ? null : s.age,
          quantity: s.quantity,
          answers: getTobaccoAnswers(isWellness, requiresTobacco, entry.tobaccoStatus),
          dependentsPartner: 0,
          dependentsChild: 0
        }))
      : [
          {
            age: isWellness ? null : age || 0,
            quantity: employees,
            answers: getTobaccoAnswers(isWellness, requiresTobacco, entry.tobaccoStatus),
            dependentsPartner: 0,
            dependentsChild: 0
          }
        ];

    const priceStructure = buildPriceStructure(addon, isVoluntary);

    const product: IPriceCalculationProductInput = {
      productName: addon.name,
      productId: addon.id,
      commission: addon.commission,
      distribution,
      priceStructure
    };

    if (group.type === CommercialAddonType.Insurance) insurance.push(product);
    else if (group.type === CommercialAddonType.Wellness) wellness.push(product);
  });

  return {
    products: {
      ...(insurance.length > 0 && { insurance }),
      ...(wellness.length > 0 && { wellness })
    }
  };
};

export const buildCensusPriceCalculationRequest = (
  addon: ICommercialAddon,
  summary: ICensusValidationResult['summary'],
  productName?: string,
  tobaccoStatus?: TobaccoStatus
): IQuotePriceCalculationInput => {
  const requiresTobacco = addonRequiresTobacco(addon);

  const distribution: IPriceCalculationDistributionInput[] = (summary ?? []).map((s) => ({
    age: s.age,
    quantity: s.quantity,
    answers: getTobaccoAnswers(false, requiresTobacco, tobaccoStatus ?? 'non_tobacco'),
    dependentsPartner: 0,
    dependentsChild: 0
  }));

  const priceStructure = buildPriceStructure(addon, false);

  return {
    products: {
      insurance: [
        {
          productName: productName ?? addon.name,
          productId: addon.id,
          commission: addon.commission,
          distribution,
          priceStructure
        }
      ]
    }
  };
};

// ─── Activation census pricing ───────────────────────────────────────────────

export const getQuoteTotal = (quote: IQuote): number =>
  round2(quote.commercialAddons.reduce((total, addon) => total + (addon.payment?.total ?? 0), 0));

export const getPriceCalculationTotal = (result: IQuotePriceCalculationResult): number =>
  round2(result.products.reduce((total, product) => total + (product.total ?? 0), 0));

export const getQuoteBundleTotal = (quote: IQuote, employees: number): number => {
  const bundleAddon = quote.commercialAddons.find((addon) => addon.entity === 'plan');
  const quotedWorkforce = quote.workforceSize ?? 0;
  if (!bundleAddon || quotedWorkforce <= 0) return 0;
  const ratePerEmployee = (bundleAddon.payment?.total ?? 0) / quotedWorkforce;
  return round2(ratePerEmployee * employees);
};

export interface ICatalogAddonEntry {
  addon: ICommercialAddon;
  type: CommercialAddonType;
  groupName: string;
}

export type CatalogAddonById = Map<number, ICatalogAddonEntry>;

export const flattenAddonCatalog = (catalog: ICommercialAddonsResponse): CatalogAddonById => {
  const byId: CatalogAddonById = new Map();
  catalog.forEach(({ type, groups }) =>
    groups.forEach(({ group, addons }) =>
      addons.forEach((addon) => byId.set(addon.id, { addon, type, groupName: group.name ?? '' }))
    )
  );
  return byId;
};

export const buildActivationCensusPriceRequest = (
  quote: IQuote,
  catalog: ICommercialAddonsResponse,
  distribution: IQuickCensusAgeSummaryEntry[]
): IQuotePriceCalculationInput => {
  const catalogById = flattenAddonCatalog(catalog);
  const products: Record<'insurance' | 'wellness' | 'voluntary', IPriceCalculationProductInput[]> = {
    insurance: [],
    wellness: [],
    voluntary: []
  };

  quote.commercialAddons.forEach((quoteAddon) => {
    if (quoteAddon.entity === 'plan') return;
    const entry = catalogById.get(quoteAddon.commercialAddonId);
    if (!entry) return;

    const { addon, type } = entry;
    const isWellness = type === CommercialAddonType.Wellness;
    const requiresTobacco = addonRequiresTobacco(addon);
    const tobaccoStatus = (quoteAddon.tobaccoStatus as TobaccoStatus) ?? 'non_tobacco';

    products[type].push({
      productName: addon.name,
      productId: addon.id,
      commission: addon.commission,
      distribution: distribution.map((entryAge) => ({
        age: isWellness ? null : entryAge.age,
        quantity: entryAge.quantity,
        answers: getTobaccoAnswers(isWellness, requiresTobacco, tobaccoStatus),
        dependentsPartner: 0,
        dependentsChild: 0
      })),
      priceStructure: buildPriceStructure(addon, type === CommercialAddonType.Voluntary)
    });
  });

  return {
    products: {
      ...(products.insurance.length > 0 && { insurance: products.insurance }),
      ...(products.wellness.length > 0 && { wellness: products.wellness }),
      ...(products.voluntary.length > 0 && { voluntary: products.voluntary })
    }
  };
};

export const resolveActivationCensusPriceMismatch = async (
  quote: IQuote,
  catalog: ICommercialAddonsResponse,
  brokerId: string | null,
  { summary, distribution }: IActivationCensusValidationErrorsResult,
  t: (key: string, params?: Record<string, unknown>) => string
): Promise<IActivationPriceMismatch | null> => {
  if (!brokerId || !distribution?.length) return null;

  const request = buildActivationCensusPriceRequest(quote, catalog, distribution);
  const hasAddonProducts = Object.keys(request.products).length > 0;
  const bundleTotal = getQuoteBundleTotal(quote, summary.total);
  if (!hasAddonProducts && bundleTotal === 0) return null;

  const originalPrice = getQuoteTotal(quote);
  const addonsPrice = hasAddonProducts
    ? getPriceCalculationTotal(await Quotes.getPriceCalculation(brokerId, request))
    : 0;
  const newPrice = round2(addonsPrice + bundleTotal);
  const headcountDelta = summary.total - (quote.workforceSize ?? 0);
  if (headcountDelta === 0 && newPrice === originalPrice) return null;

  const { priceMismatchReasonMore, priceMismatchReasonFewer, priceMismatchReasonAges } =
    TRANSLATIONS_PROSPECTS.profile.activationSetup.censusModal;
  const reasonKey = headcountDelta > 0 ? priceMismatchReasonMore : priceMismatchReasonFewer;

  return {
    originalPrice,
    newPrice,
    reason: headcountDelta === 0 ? t(priceMismatchReasonAges) : t(reasonKey, { count: Math.abs(headcountDelta) })
  };
};

export const buildInitialCalculatedPrice = (
  isEditing: boolean,
  editingQuote: IQuote | null
): IQuotePriceCalculationResult | null => {
  if (!isEditing || !editingQuote) return null;
  return {
    products: editingQuote.commercialAddons
      .filter((addon) => addon.entity !== 'plan')
      .map((addon) => ({
        productId: addon.commercialAddonId,
        productName: '',
        productType: '',
        total: addon.payment?.total ?? 0,
        commission_total: addon.payment?.commission ?? 0,
        distribution: []
      }))
  };
};

export const checkHasNullPayments = (isEditing: boolean, editingQuote: IQuote | null): boolean => {
  return (
    isEditing &&
    (editingQuote?.commercialAddons.some(
      (addon) => addon.payment?.total == null || addon.payment?.commission == null
    ) ??
      false)
  );
};

export const getAgeValid = (ageNum: number, groups: IProductGroup[], selection: SelectionState): boolean => {
  if (ageNum <= 0) return false;
  const ageDependentGroups = groups.filter(
    (group) =>
      selection[group.key]?.selected &&
      group.employerPaid &&
      addonIsAgeDependent(group.addons.find((a) => a.id === selection[group.key]?.addonId)) &&
      selection[group.key]?.ageInputMode === 'average'
  );
  return ageDependentGroups.every((group) => {
    const addon = group.addons.find((a) => a.id === selection[group.key]?.addonId);
    const range = getAddonAgeRange(addon);
    return range ? ageNum >= range.min && ageNum <= range.max : ageNum >= MIN_AGE && ageNum <= MAX_AGE;
  });
};

export const getAverageAgeError = (
  averageAge: string,
  ageValid: boolean,
  groups: IProductGroup[],
  selection: SelectionState,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null => {
  if (averageAge === '' || ageValid) return null;
  const ageDependentAddon = groups
    .filter((group) => {
      if (!selection[group.key]?.selected || !group.employerPaid) return false;
      const addon = group.addons.find((a) => a.id === selection[group.key]?.addonId);
      return addonIsAgeDependent(addon);
    })
    .map((group) => group.addons.find((a) => a.id === selection[group.key]?.addonId))
    .find(Boolean);
  if (!ageDependentAddon) return null;
  const range = getAddonAgeRange(ageDependentAddon);
  return t(TRANSLATIONS_QUOTES.create.group.averageAgeRange, {
    min: range?.min ?? MIN_AGE,
    max: range?.max ?? MAX_AGE
  });
};

interface ICheckCanSaveParams {
  employeesNum: number;
  minEmployees: number;
  selectedCount: number;
  requiresProductSelection: boolean;
  bundleRequired: boolean;
  quoteName: string;
  ageRequired: boolean;
  ageValid: boolean;
  hasMissingRate: boolean;
  saving: boolean;
  isEditDataMissing: boolean;
}

export const checkCanSave = ({
  employeesNum,
  minEmployees,
  selectedCount,
  requiresProductSelection,
  bundleRequired,
  quoteName,
  ageRequired,
  ageValid,
  hasMissingRate,
  saving,
  isEditDataMissing
}: ICheckCanSaveParams): boolean => {
  return (
    employeesNum >= minEmployees &&
    !bundleRequired &&
    (!requiresProductSelection || selectedCount > 0) &&
    quoteName.trim() !== '' &&
    (!ageRequired || ageValid) &&
    !hasMissingRate &&
    !saving &&
    !isEditDataMissing
  );
};

interface IGetSaveTooltipParams {
  isEditDataMissing: boolean;
  employeesNum: number;
  minEmployees: number;
  quoteName: string;
  selectedCount: number;
  requiresProductSelection: boolean;
  bundleRequired: boolean;
  ageRequired: boolean;
  ageValid: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export const getSaveTooltip = ({
  isEditDataMissing,
  employeesNum,
  minEmployees,
  quoteName,
  selectedCount,
  requiresProductSelection,
  bundleRequired,
  ageRequired,
  ageValid,
  t
}: IGetSaveTooltipParams): string | undefined => {
  if (isEditDataMissing) return t(TRANSLATIONS_QUOTES.create.notFound);
  if (employeesNum < minEmployees) {
    return t(TRANSLATIONS_QUOTES.create.tooltips.employeesRequired, { count: minEmployees });
  }
  if (bundleRequired) return t(TRANSLATIONS_QUOTES.create.tooltips.chooseBundle);
  if (quoteName.trim() === '') return t(TRANSLATIONS_QUOTES.create.tooltips.quoteNameRequired);
  if (requiresProductSelection && selectedCount === 0) return t(TRANSLATIONS_QUOTES.create.tooltips.selectProduct);
  if (ageRequired && !ageValid) return t(TRANSLATIONS_QUOTES.create.tooltips.ageRequired);
  return undefined;
};

export interface AgeBandBreakdownRow {
  ageFrom: number;
  ageTo: number;
  count: number;
}

export function computeAvgAge(summary: { age: number; quantity: number }[] | null | undefined): number | null {
  if (!summary?.length) return null;
  const totalPeople = summary.reduce((acc, s) => acc + s.quantity, 0);
  if (totalPeople === 0) return null;
  const weightedSum = summary.reduce((acc, s) => acc + s.age * s.quantity, 0);
  return Math.round(weightedSum / totalPeople);
}

interface AgeBandSpec {
  label: string;
  from: number;
  to: number;
}

export const AGE_BANDS_SPEC: AgeBandSpec[] = [
  { label: '18-29', from: 18, to: 29 },
  { label: '30-39', from: 30, to: 39 },
  { label: '40-49', from: 40, to: 49 },
  { label: '50-59', from: 50, to: 59 },
  { label: '60+', from: 60, to: 999 }
];

export const getAddonAgeBands = (addon: ICommercialAddon | null | undefined): { from: number; to: number }[] => {
  const tiers = addon?.priceStructure?.structure ?? [];
  const seen = new Set<string>();
  const bands: { from: number; to: number }[] = [];
  for (const tier of tiers) {
    const key = `${tier.age.from}-${tier.age.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    bands.push({ from: tier.age.from, to: tier.age.to });
  }
  return bands.sort((a, b) => a.from - b.from);
};

export function groupSummaryIntoBands(
  summary: { age: number; quantity: number }[],
  addon?: ICommercialAddon | null
): AgeBandBreakdownRow[] {
  const addonBands = getAddonAgeBands(addon);
  const bands = addonBands.length ? addonBands : AGE_BANDS_SPEC.map((band) => ({ from: band.from, to: band.to }));
  return bands
    .map((band) => {
      const count = summary
        .filter((s) => s.age >= band.from && s.age <= band.to)
        .reduce((acc, s) => acc + s.quantity, 0);
      return { ageFrom: band.from, ageTo: band.to, count };
    })
    .filter((b) => b.count > 0);
}

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_RETRY_INTERVAL = 2000;

export interface DownloadQuotePdfResult {
  documentUrl: string;
}

export interface DownloadQuotePdfOptions {
  maxRetries?: number;
  retryInterval?: number;
  signal?: AbortSignal;
}

export const isAbortError = (err: unknown): boolean => {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return err.name === 'AbortError';
  }
  if (err instanceof Error) {
    return err.name === 'AbortError' || err.name === 'CanceledError';
  }
  return false;
};

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    function onAbort() {
      clearTimeout(id);
      reject(signal!.reason);
    }

    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const downloadQuotePdf = async (
  brokerId: string,
  prospectId: number,
  quoteId: number,
  options?: DownloadQuotePdfOptions
): Promise<DownloadQuotePdfResult> => {
  const maxRetries = Math.max(1, options?.maxRetries ?? DEFAULT_MAX_RETRIES);
  const retryInterval = options?.retryInterval ?? DEFAULT_RETRY_INTERVAL;
  const { signal } = options ?? {};

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    signal?.throwIfAborted();

    try {
      const quotes = await Quotes.getQuotes(brokerId, prospectId, { signal });
      lastError = undefined;
      const file = quotes.find((q) => q.id === quoteId)?.files.find((f) => f.fileType === 'quote');

      if (file?.documentUrl) {
        return { documentUrl: file.documentUrl };
      }
    } catch (err) {
      if (isAbortError(err)) throw err;
      lastError = err;
    }

    if (attempt < maxRetries) {
      await delay(retryInterval, signal);
    }
  }

  throw lastError ?? new Error('PDF not available after retries');
};

export const censusVariantFor = (isAppOnly: boolean): CensusVariant =>
  isAppOnly ? CensusVariant.AppOnly : CensusVariant.Insurance;
